var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
// Import the CSS for different display styles
import { ProfileDisplayCss } from './styles/profile.css';
import { VideoDisplayCss } from './styles/video.css';
import { UploaderDisplayCss } from './styles/uploader.css';
import { DocumentDisplayCss } from './styles/document.css';
import { ImageDisplayCss } from './styles/image.css';
let FileUpload = class FileUpload extends LitElement {
    constructor() {
        super(...arguments);
        /** Explicit mode for backend routing: 'hierarchical' | 'single' */
        this.mode = 'hierarchical';
        /** Context path for single mode or hierarchical segmentation */
        this.context = '';
        /** Section for whitelabel mode */
        this.section = '';
        /**
         * If true, disables the upload functionality, making the component a viewer.
         * It will only display an existing file and will not show any upload prompts.
         */
        this.noUpload = false;
        /**
         * If true, prevents changing or removing a file once it has been successfully loaded.
         */
        this.noEdit = false;
        this.apiUrl = '/ui/media';
        this.downloadUrl = '/ui/media';
        this.clientName = '';
        this.requester = '';
        this.requesterId = '';
        this.fileName = ''; // Base name for the file, e.g., 'profile'
        this.display = 'uploader';
        this.allowedFileTypes = 'image/*,video/*,application/pdf';
        this.size = 100; // Used for UIs where size is relevant (e.g., profile)
        this.initialFileExtension = '';
        // If you want the component to have a transparent background...
        this.transparentBackground = false;
        // --- INTERNAL STATE ---
        this.uploadState = 'idle';
        this.previewUrl = null;
        this.isDragging = false;
        this.errorMessage = '';
        this.uploadedFileName = '';
        this.uploadedFileType = 'generic';
        this.uploadedMimeType = '';
    }
    /**
     * If key properties change, reload the preview from the server.
     */
    updated(changedProperties) {
        if (changedProperties.has('clientName') ||
            changedProperties.has('requesterId') ||
            changedProperties.has('fileName') ||
            changedProperties.has('downloadUrl')) {
            this.loadInitialPreview();
        }
    }
    /**
     * On first load, attempt to fetch the existing file for preview.
     */
    firstUpdated() {
        this.loadInitialPreview();
    }
    buildUploadUrl(fileCategory, fileName, originalName) {
        const base = '/ui/media';
        // Whitelabel mode: hostname comes from URL, not from context
        if (this.mode === 'whitelabel') {
            const section = this.section || 'images';
            const path = `${base}/whitelabel/upload/${section}/${encodeURIComponent(fileName)}/`;
            const params = new URLSearchParams();
            if (originalName)
                params.set('original_name', originalName);
            return `${path}?${params.toString()}`;
        }
        // Standard mode with client_name, requester
        const client = this.clientName;
        const requester = this.requester;
        const requesterId = this.requesterId;
        const typedSegment = `upload_${fileCategory}`;
        const path = requesterId
            ? `${base}/${typedSegment}/${client}/${requester}/${requesterId}/${encodeURIComponent(fileName)}/`
            : `${base}/${typedSegment}/${client}/${requester}/${encodeURIComponent(fileName)}/`;
        const params = new URLSearchParams();
        params.set('mode', this.mode);
        if (this.mode === 'single') {
            params.set('context', this.context);
        }
        else {
            params.set('media_type', fileCategory);
            if (this.context)
                params.set('context', this.context);
            if (this.section)
                params.set('section', this.section);
        }
        if (originalName)
            params.set('original_name', originalName);
        return `${path}?${params.toString()}`;
    }
    buildDownloadUrl(fileCategory, fileName) {
        const base = this.downloadUrl || '/ui/media';
        // Whitelabel mode: hostname comes from URL, not from context
        if (this.mode === 'whitelabel') {
            const section = this.section || 'images';
            const path = `${base}/whitelabel/download/${section}/${encodeURIComponent(fileName)}/`;
            const params = new URLSearchParams();
            params.set('t', Date.now().toString());
            return `${path}?${params.toString()}`;
        }
        // Standard mode with client_name, requester
        const client = this.clientName;
        const requester = this.requester;
        const requesterId = this.requesterId;
        const typedSegment = `download_${fileCategory}`;
        const path = requesterId
            ? `${base}/${typedSegment}/${client}/${requester}/${requesterId}/${encodeURIComponent(fileName)}/`
            : `${base}/${typedSegment}/${client}/${requester}/${encodeURIComponent(fileName)}/`;
        const params = new URLSearchParams();
        params.set('mode', this.mode);
        if (this.mode === 'single') {
            params.set('context', this.context);
        }
        else {
            params.set('media_type', fileCategory);
            if (this.context)
                params.set('context', this.context);
            if (this.section)
                params.set('section', this.section);
        }
        // Add cache buster
        params.set('t', Date.now().toString());
        return `${path}?${params.toString()}`;
    }
    /**
     * Dispatches a `content-changed` event whenever the upload state or content changes.
     * Includes file name, preview URL, and current state in the event detail.
     */
    _notifyContentChanged() {
        this.dispatchEvent(new CustomEvent('content-changed', {
            detail: {
                fileName: this.uploadedFileName,
                previewUrl: this.previewUrl,
                state: this.uploadState
            },
            bubbles: true, // so parent components can listen
            composed: true // allow event to cross shadow DOM
        }));
    }
    /**
     * Determines file category from a file extension string.
     */
    _getCategoryFromExtension(extension) {
        const ext = extension.toLowerCase();
        if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff'].includes(ext))
            return 'image';
        if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(ext))
            return 'video';
        if (['pdf'].includes(ext))
            return 'document';
        return 'generic';
    }
    /**
     * Determines the primary file category based on the component's configuration.
     * This is used to construct the correct initial download URL.
     */
    _getInitialFileCategory() {
        // Priority 1: Explicit display mode
        if (this.display === 'video')
            return 'video';
        if (this.display === 'document')
            return 'document';
        if (this.display === 'image')
            return 'image';
        // Priority 2: Explicit initial file extension
        if (this.initialFileExtension) {
            const category = this._getCategoryFromExtension(this.initialFileExtension);
            if (category !== 'generic')
                return category;
        }
        // Priority 3: Infer from allowed types for profile/uploader
        if (this.display === 'profile' || this.display === 'uploader') {
            if (this.allowedFileTypes.includes('image'))
                return 'image';
            if (this.allowedFileTypes.includes('video'))
                return 'video';
        }
        return 'generic'; // Fallback
    }
    _getFileCategory(file) {
        const mimeType = file.type;
        const extension = (file.name.split('.').pop() || '').toLowerCase();
        // Prioritize MIME type for standard web formats
        if (mimeType.startsWith('image/'))
            return 'image';
        if (mimeType.startsWith('video/'))
            return 'video';
        if (mimeType === 'application/pdf')
            return 'document';
        // Fallback to extension check if MIME is generic or missing
        if (['pdf'].includes(extension)) {
            return 'document';
        }
        // Fallback for common image/video extensions
        if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'tiff'].includes(extension))
            return 'image';
        if (['mp4', 'mov', 'avi', 'webm', 'mkv'].includes(extension))
            return 'video';
        return 'generic';
    }
    /**
     * Constructs the URL for an existing file on the server and applies a cache-busting
     * timestamp to ensure the latest version is displayed.
     */
    loadInitialPreview() {
        if (this.mode === 'whitelabel') {
            if (!this.fileName) {
                this.previewUrl = null;
                return;
            }
        }
        else {
            if (!this.clientName || !this.requester || !this.fileName) {
                this.previewUrl = null;
                return;
            }
        }
        const category = this._getInitialFileCategory();
        if (category === 'generic') {
            this.previewUrl = null;
            return;
        }
        const previewExtension = this.initialFileExtension ||
            (category === 'image'
                ? 'webp'
                : category === 'video'
                    ? 'mp4'
                    : category === 'document'
                        ? 'pdf'
                        : '');
        if (!previewExtension) {
            this.previewUrl = null;
            return;
        }
        this.uploadedFileType = category;
        this.previewUrl =
            this.buildDownloadUrl(category, `${this.fileName}.${previewExtension}`) +
                `&t=${Date.now()}`;
        if (this.previewUrl && ['image', 'video', 'document'].includes(this.display)) {
            this.uploadState = 'success';
            this.uploadedFileName = `${this.fileName}.${previewExtension}`;
        }
    }
    _handleClick() { this.shadowRoot?.getElementById('file-input')?.click(); }
    _handleDragOver(e) { e.preventDefault(); this.isDragging = true; }
    _handleDragLeave(e) { e.preventDefault(); this.isDragging = false; }
    _handleDrop(e) {
        e.preventDefault();
        this.isDragging = false;
        const files = e.dataTransfer?.files;
        if (files && files.length > 0)
            this._uploadFile(files[0]);
    }
    _handleFileChange(e) {
        const input = e.target;
        if (input.files && input.files.length > 0)
            this._uploadFile(input.files[0]);
    }
    _resetState() {
        this.uploadState = 'idle';
        this.previewUrl = null;
        this.errorMessage = '';
        this.uploadedFileName = '';
        // Reload the initial preview in case a file exists on the server
        this.loadInitialPreview();
        this._notifyContentChanged();
    }
    async _uploadFile(file) {
        this.uploadedFileName = file.name;
        this.uploadedMimeType = file.type;
        const fileCategory = this._getFileCategory(file);
        this.uploadedFileType = fileCategory;
        if (fileCategory === 'generic') {
            this.errorMessage = `Invalid or unsupported file type. Please upload a valid file.`;
            this.uploadState = 'error';
            this._notifyContentChanged();
            return;
        }
        this.uploadState = 'uploading';
        try {
            const originalExtension = file.name.split('.').pop() || '';
            const staticName = this.fileName ? `${this.fileName}.${originalExtension}` : file.name;
            // ✅ Decide which name to use for the actual file
            const renamedFile = new File([file], staticName, { type: file.type });
            // ✅ Build upload URL and include original name as query param
            const uploadUrl = this.buildUploadUrl(fileCategory, renamedFile.name, file.name);
            const formData = new FormData();
            formData.append('file', renamedFile);
            formData.append('original_name', file.name); // ✅ Send original name too
            const response = await fetch(uploadUrl, { method: 'POST', body: formData });
            if (response.ok) {
                this.uploadState = 'success';
                const previewExtension = fileCategory === 'image' ? 'webp' : originalExtension;
                this.previewUrl = this.buildDownloadUrl(fileCategory, `${this.fileName}.${previewExtension}`) + `&t=${Date.now()}`;
            }
            else {
                this.errorMessage = (await response.text()) || 'Upload failed due to a server error.';
                this.uploadState = 'error';
            }
        }
        catch (error) {
            this.errorMessage = error.message;
            this.uploadState = 'error';
        }
        this._notifyContentChanged();
    }
    render() {
        const sharedInput = html `<input type="file" id="file-input" accept=${this.allowedFileTypes} @change=${this._handleFileChange}>`;
        switch (this.display) {
            case 'profile':
                return this.renderProfileUI(sharedInput);
            case 'uploader':
                return this.renderUploaderUI(sharedInput);
            case 'video':
                return this.renderVideoUI(sharedInput);
            case 'document':
                return this.renderDocumentUI(sharedInput);
            case 'image':
                return this.renderImageUI(sharedInput);
            default:
                return this.renderUploaderUI(sharedInput);
        }
    }
    renderImageUI(inputElement) {
        if (this.noUpload && this.uploadState !== 'success') {
            return html ``;
        }
        let content;
        const containerStyles = {
            minWidth: '250px',
        };
        if (this.size !== 100) {
            containerStyles.maxWidth = `${this.size}px`;
        }
        const isAttemptingLoad = this.uploadState === 'idle' && this.previewUrl;
        switch (this.uploadState) {
            case 'success':
                content = html `
                    <div class="image-preview-wrapper">
                        <img class="image-preview" src=${this.previewUrl} alt="Uploaded preview">
                        ${!this.noEdit ? html `
                            <div class="image-overlay">
                            <button class="overlay-button" @click=${this._handleClick}>
                                <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                                Change
                            </button>
                        </div>
                        ` : ''}
                    </div>
                `;
                break;
            case 'uploading':
                content = html `<div class="loader"></div><div class="prompt">Uploading Image...</div>`;
                break;
            case 'error':
                content = html `
                    <svg class="icon error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                    <div class="prompt">Upload Failed</div>
                    <div class="error-message">${this.errorMessage}</div>
                    <button class="try-again-button" @click=${this._resetState}>Try Again</button>
                `;
                break;
            case 'idle':
            default:
                content = html `
                    <svg 
                        class="icon" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                    </svg>
                    <div class="prompt">Drag & Drop an image</div>
                    <div class="support-text">or click to select</div>
                `;
                break;
        }
        return html `
            <div 
                class="image-container ${classMap({
            'dragging-over': this.isDragging,
            'has-preview': this.uploadState === 'success' || isAttemptingLoad,
            'transparent-bg': this.transparentBackground
        })}"
                style=${styleMap(containerStyles)}
                @click=${(this.uploadState === 'idle' && !this.previewUrl && !this.noUpload) ? this._handleClick : null}
                @dragover=${!this.noUpload ? this._handleDragOver : null}
                @dragleave=${!this.noUpload ? this._handleDragLeave : null}
                @drop=${!this.noUpload ? this._handleDrop : null}
            >
                ${inputElement}
                <div class="content-wrapper">${content}</div>
            </div>
        `;
    }
    renderProfileUI(inputElement) {
        if (this.noUpload && this.uploadState !== 'success') {
            return html ``;
        }
        const containerStyles = {
            width: `${this.size}px`,
            height: `${this.size}px`,
        };
        let previewContent;
        if (this.previewUrl && this.uploadState !== 'error') {
            if (this.uploadedFileType === 'video') {
                previewContent = html `<video class="preview-media" src=${this.previewUrl} autoplay muted loop playsinline></video>`;
            }
            else {
                previewContent = html `<img class="preview-media" src=${this.previewUrl} alt="Profile">`;
            }
        }
        else {
            previewContent = html `<svg class="default-avatar" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>`;
        }
        let overlayContent;
        if (!this.noEdit) {
            switch (this.uploadState) {
                case 'uploading':
                    overlayContent = html `<div class="loader"></div>`;
                    break;
                case 'error':
                    overlayContent = html `<svg class="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>`;
                    break;
                default:
                    overlayContent = html `<svg class="camera-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.776 48.776 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" /><path stroke-linecap="round" stroke-linejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" /></svg>`;
                    break;
            }
        }
        return html `
            <div 
                class="profile-container ..."
                style=${styleMap(containerStyles)}
                @click=${(!this.noUpload && !this.noEdit && this.uploadState !== 'uploading') ? () => this.uploadState === 'error' ? this._resetState() : this._handleClick() : null}
                @dragover=${!this.noUpload && !this.noEdit ? this._handleDragOver : null}
                @dragleave=${!this.noUpload && !this.noEdit ? this._handleDragLeave : null}
                @drop=${!this.noUpload && !this.noEdit ? this._handleDrop : null}
                title=${this.uploadState === 'error' ? this.errorMessage : ''}
            >
                ${inputElement}
                ${previewContent}
                <div class="overlay">${overlayContent}</div>
            </div>
        `;
    }
    renderUploaderUI(inputElement) {
        let content;
        switch (this.uploadState) {
            case 'uploading':
                content = html `<div class="loader"></div><div class="prompt">Uploading...</div><div class="file-name">${this.uploadedFileName}</div>`;
                break;
            case 'success':
                content = html `<svg class="success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><div class="prompt">Upload Complete!</div><div class="file-name">${this.uploadedFileName}</div><button class="try-again-button" @click=${this._resetState}>Upload Another</button>`;
                break;
            case 'error':
                content = html `<svg class="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg><div class="prompt">Upload Failed</div><div class="error-message">${this.errorMessage}</div><button class="try-again-button" @click=${this._resetState}>Try Again</button>`;
                break;
            case 'idle':
            default:
                content = html `
                    <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 16.5V9.75m0 0l-3 3m3-3l3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
                    </svg>
                    <div class="prompt">Drag & Drop your file here</div>
                    <div class="support-text">or click to select a file</div>
                `;
        }
        return html `<div class="uploader-container ${classMap({ 'dragging-over': this.isDragging })}" @click=${this.uploadState === 'idle' || this.uploadState === 'error' ? this._handleClick : null} @dragover=${this._handleDragOver} @dragleave=${this._handleDragLeave} @drop=${this._handleDrop}>${inputElement}<div class="content-wrapper">${content}</div></div>`;
    }
    renderVideoUI(inputElement) {
        if (this.noUpload && this.uploadState !== 'success') {
            return html ``;
        }
        const containerStyles = {
            minWidth: '400px',
            width: `${this.size}px`,
            maxWidth: '100%',
        };
        let content;
        switch (this.uploadState) {
            case 'success':
                content = html `
                    <div class="video-preview-wrapper">
                        <video class="video-preview" src=${this.previewUrl} controls></video>
                        ${!this.noEdit ? html `
                            <div class="video-overlay">
                                <button class="overlay-button" @click=${this._handleClick}>
                                    <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0l1.513 1.513M21.75 14.25v2.25c0 1.03-.84 1.875-1.875 1.875H5.625c-1.03 0-1.875-.84-1.875-1.875v-10.5c0-1.03.84-1.875 1.875-1.875h7.5" />
                                    </svg>
                                    Change Video
                                </button>
                            </div>
                        ` : ''}
                    </div>`;
                break;
            case 'uploading':
                content = html `<div class="loader"></div><div class="prompt">Uploading Video...</div>`;
                break;
            case 'error':
                content = html `<svg class="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg><div class="prompt">Upload Failed</div><div class="error-message">${this.errorMessage}</div><button class="try-again-button" @click=${this._resetState}>Try Again</button>`;
                break;
            case 'idle':
            default:
                content = html `
                    <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0013.5 5.25h-9A2.25 2.25 0 002.25 7.5v9A2.25 2.25 0 004.5 18.75z" />
                    </svg>
                    <div class="prompt">Drop a video file here</div>
                    <div class="support-text">16:9 aspect ratio recommended</div>
                `;
        }
        return html `
            <div 
                class="video-container ${classMap({ 'dragging-over': this.isDragging })}"
                style=${styleMap(containerStyles)}
                @click=${this.uploadState === 'idle' && !this.noUpload ? this._handleClick : null}
                @dragover=${!this.noUpload ? this._handleDragOver : null}
                @dragleave=${!this.noUpload ? this._handleDragLeave : null}
                @drop=${!this.noUpload ? this._handleDrop : null}
            >
                ${inputElement}
                <div class="content-wrapper">${content}</div>
            </div>
        `;
    }
    renderDocumentUI(inputElement) {
        if (this.noUpload && this.uploadState !== 'success') {
            return html ``;
        }
        const containerStyles = this.size !== 100
            ? { height: `${this.size}px` }
            : {};
        let content;
        switch (this.uploadState) {
            case 'success':
                content = html `
                    <div class="document-preview-wrapper">
                        <iframe class="document-preview-frame" src=${this.previewUrl}></iframe>
                        ${!this.noEdit ? html `
                            <div class="document-overlay">
                                <button class="overlay-button" @click=${this._handleClick}>
                                    <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.5L15.232 5.232z"></path></svg>
                                    Change
                                </button>
                            </div>
                        ` : ''}
                    </div>
                `;
                break;
            case 'error':
                content = html `
                    <div class="content-wrapper">
                        <svg class="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
                        <div class="details">
                            <div class="prompt">Upload Failed</div>
                            <div class="error-message">${this.errorMessage}</div>
                        </div>
                        ${!this.noEdit ? html `
                            <button class="try-again-button" @click=${this._resetState}>Try Again</button>
                        ` : ''}
                    </div>
                `;
                break;
            case 'uploading':
                content = html `
                        <div class="loader"></div>
                        <div class="details">
                            <div class="prompt">Uploading Document...</div>
                            <div class="file-name">${this.uploadedFileName}</div>
                        </div>
                    `;
                break;
            default:
                content = html `
                        <svg class="icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"></path>
                        </svg>
                        <div class="details">
                            <div class="prompt">Drag & Drop your document</div>
                            <div class="support-text">Supports PDFs only</div>
                        </div>
                    `;
        }
        return html `
            <div 
                class="document-container ${classMap({
            'dragging-over': this.isDragging,
            'has-preview': this.uploadState === 'success'
        })}"
                style=${styleMap(containerStyles)}
                @click=${(this.uploadState === 'idle' && !this.noUpload) ? this._handleClick : null}
                @dragover=${!this.noUpload ? this._handleDragOver : null} 
                @dragleave=${!this.noUpload ? this._handleDragLeave : null} 
                @drop=${!this.noUpload ? this._handleDrop : null}
            >
                ${inputElement}
                ${content}
            </div>
        `;
    }
};
// Combine styles from different modules. The component's DOM will be scoped.
FileUpload.styles = [
    // Common styles can be added here
    css `
            :host { display: block; }
            input[type="file"] { display: none; }
        `,
    ProfileDisplayCss,
    UploaderDisplayCss,
    VideoDisplayCss,
    DocumentDisplayCss,
    ImageDisplayCss
];
__decorate([
    property({ type: String, attribute: 'mode' })
], FileUpload.prototype, "mode", void 0);
__decorate([
    property({ type: String, attribute: 'context' })
], FileUpload.prototype, "context", void 0);
__decorate([
    property({ type: String, attribute: 'section' })
], FileUpload.prototype, "section", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-upload' })
], FileUpload.prototype, "noUpload", void 0);
__decorate([
    property({ type: Boolean, attribute: 'no-edit' })
], FileUpload.prototype, "noEdit", void 0);
__decorate([
    property({ type: String, attribute: 'api-url' })
], FileUpload.prototype, "apiUrl", void 0);
__decorate([
    property({ type: String, attribute: 'download-url' })
], FileUpload.prototype, "downloadUrl", void 0);
__decorate([
    property({ type: String, attribute: 'client-name' })
], FileUpload.prototype, "clientName", void 0);
__decorate([
    property({ type: String })
], FileUpload.prototype, "requester", void 0);
__decorate([
    property({ type: String, attribute: 'requester-id' })
], FileUpload.prototype, "requesterId", void 0);
__decorate([
    property({ type: String, attribute: 'file-name' })
], FileUpload.prototype, "fileName", void 0);
__decorate([
    property({ type: String })
], FileUpload.prototype, "display", void 0);
__decorate([
    property({ type: String, attribute: 'allowed-file-types' })
], FileUpload.prototype, "allowedFileTypes", void 0);
__decorate([
    property({ type: Number })
], FileUpload.prototype, "size", void 0);
__decorate([
    property({ type: String, attribute: 'initial-file-extension' })
], FileUpload.prototype, "initialFileExtension", void 0);
__decorate([
    property({ type: Boolean, attribute: 'transparent-background' })
], FileUpload.prototype, "transparentBackground", void 0);
__decorate([
    state()
], FileUpload.prototype, "uploadState", void 0);
__decorate([
    state()
], FileUpload.prototype, "previewUrl", void 0);
__decorate([
    state()
], FileUpload.prototype, "isDragging", void 0);
__decorate([
    state()
], FileUpload.prototype, "errorMessage", void 0);
__decorate([
    state()
], FileUpload.prototype, "uploadedFileName", void 0);
__decorate([
    state()
], FileUpload.prototype, "uploadedFileType", void 0);
__decorate([
    state()
], FileUpload.prototype, "uploadedMimeType", void 0);
FileUpload = __decorate([
    customElement('file-upload')
], FileUpload);
export { FileUpload };
